import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "../page.css";
import "../home.css";
import "./kilimanjaro.css";
import "./meru.css";

const features = [
  {
    title: "Socialist Peak, 4,566 m",
    text: "Tanzania’s second highest summit. Steeper than Kilimanjaro in places, with a shorter overall schedule.",
  },
  {
    title: "Arusha National Park",
    text: "The climb starts inside the park. Rangers join early days when wildlife can share the lower trails.",
  },
  {
    title: "Crater and ash cone",
    text: "You walk the crater rim toward the summit, with views down to the ash cone and out toward Kilimanjaro on clear mornings.",
  },
  {
    title: "Strong warmup climb",
    text: "Many groups climb Meru before Kilimanjaro to test gear, pacing, and how the body feels at altitude.",
  },
];

const routes = [
  {
    id: "momella",
    name: "Momella Route",
    days: "3 to 4 days",
    difficulty: "Challenging",
    lodging: "Mountain huts",
    startGate: "Momella Gate",
    bestFor: "The standard Meru ascent",
    image:
      "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open highland landscape near Arusha National Park",
    summary:
      "The established path through Arusha National Park. You rise from forest and grassland to Miriakamba and Saddle Huts, then follow the crater rim to Socialist Peak.",
    body: "Momella is the route almost every Meru climb follows. Lower days can include buffalo, giraffe, and colobus monkeys, so an armed ranger walks with the group until the higher camps. The trail steepens after Miriakamba, and summit night from Saddle Hut is a long, cold ridge walk with little shelter from the wind.",
    highlights: [
      "Park start with possible wildlife on the approach",
      "Hut lodging at Miriakamba and Saddle",
      "Crater rim traverse with ash cone views",
      "Often clear sightlines toward Kilimanjaro at dawn",
    ],
    itinerary: [
      { day: "Day 1", text: "Momella Gate to Miriakamba Hut through forest and open glades." },
      { day: "Day 2", text: "Climb to Saddle Hut, optional walk toward Little Meru." },
      { day: "Day 3", text: "Midnight start along the rim to Socialist Peak, descend to Miriakamba." },
      { day: "Day 4", text: "Walk out to Momella Gate, or combine with a shorter three day plan." },
    ],
    notes:
      "This is the line used for both the three day and four day programs. Duration is what changes, not the path.",
  },
  {
    id: "meru-3-day",
    name: "3 Day Meru Trek",
    days: "3 days",
    difficulty: "Challenging",
    lodging: "Mountain huts",
    startGate: "Momella Gate",
    bestFor: "Fit hikers on a tight schedule",
    image:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "High mountain ridge under open sky",
    summary:
      "A compact summit schedule on the Momella Route. You move steadily from gate to Saddle, summit on the second night into the third morning, then walk out.",
    body: "Three days works when the group is fit, already used to hiking, and short on time before a safari or a Kilimanjaro climb. There is less room to rest if anyone feels the altitude, so we screen carefully and keep the pace honest. Summit night remains early, cold, and exposed on the rim.",
    highlights: [
      "Faster program with the same summit goal",
      "Good when Meru is a short add on before another trip",
      "Less recovery time between hut days",
      "Best for strong hikers with recent trail fitness",
    ],
    itinerary: [
      { day: "Day 1", text: "Momella Gate to Miriakamba Hut." },
      { day: "Day 2", text: "Miriakamba to Saddle Hut, short acclimatization walk if energy allows." },
      { day: "Day 3", text: "Summit Socialist Peak, descend past Miriakamba, exit Momella Gate." },
    ],
    notes:
      "We suggest the four day plan if anyone in the group is new to altitude or prefers a gentler finish after summit night.",
  },
  {
    id: "meru-4-day",
    name: "4 Day Meru Trek",
    days: "4 days",
    difficulty: "Challenging",
    lodging: "Mountain huts",
    startGate: "Momella Gate",
    bestFor: "Extra rest and stronger summit form",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Quiet alpine slopes on a mountain approach",
    summary:
      "The same Momella Route with an extra night that spreads the effort. You gain height more gently and usually descend to a lower hut after the summit before walking out.",
    body: "Four days is our preferred Meru plan for most private groups. The added night helps legs and sleep before the rim, and the walk out feels less rushed after a long summit morning. It is also the better choice when Meru is your main mountain rather than a quick warmup.",
    highlights: [
      "More time at Saddle before summit night",
      "Optional Little Meru walk for light acclimatization",
      "Gentler exit day after the summit",
      "Stronger choice for first high altitude treks",
    ],
    itinerary: [
      { day: "Day 1", text: "Momella Gate to Miriakamba Hut." },
      { day: "Day 2", text: "Climb to Saddle Hut, optional Little Meru viewpoint." },
      { day: "Day 3", text: "Summit Socialist Peak at dawn, descend to Miriakamba for rest." },
      { day: "Day 4", text: "Forest walk to Momella Gate and transfer to Arusha." },
    ],
    notes:
      "Ask for this plan if Meru is your first summit above 4,000 m, or if you want clearer Kilimanjaro views with less fatigue.",
  },
];

const practical = [
  {
    title: "Best months",
    text: "June to October, and January to early March, when trails are usually drier and views toward Kilimanjaro are clearer.",
  },
  {
    title: "Fitness",
    text: "Expect steep days and an early summit start. Prior hill walking helps more than gym speed. Poles are useful on the descent.",
  },
  {
    title: "What we arrange",
    text: "Park fees, guides, hut stays, meals on the mountain, ranger cover on lower trails, and transfers from Arusha.",
  },
];

function Meru() {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = "Mount Meru · Mirrorkhan Tours";
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);

  return (
    <div className="kili-page meru-page">
      <section className="kili-hero meru-hero">
        <div className="kili-hero__media" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1800&q=80"
            alt=""
          />
        </div>
        <div className="kili-hero__shade" aria-hidden="true" />
        <div className="container kili-hero__content">
          <div className="row">
            <div className="col-lg-8 col-xl-7 mk-hero-enter">
              <p className="eyebrow eyebrow--light mb-3">
                <Link to="/trekking" className="text-decoration-none">
                  Trekking
                </Link>
              </p>
              <h1 className="mb-3">Mount Meru</h1>
              <p className="kili-hero__lede mb-4">
                A sharp, scenic climb in Arusha National Park. Stand alone as a
                summit trip, or use it as a focused warmup before Kilimanjaro.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a className="btn btn-mk-gold" href="#routes">
                  View routes
                </a>
                <Link className="btn btn-mk-ghost" to="/contact">
                  Ask about Meru
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal>
            <div className="col-lg-8">
              <p className="eyebrow">The mountain</p>
              <h2 className="mb-3">Why Meru earns its own trip</h2>
              <p className="page-lead mb-0">
                It is steeper and more intimate than Kilimanjaro, with park
                wildlife on the approach and a crater rim that feels close and
                exposed on summit night.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((item, index) => (
              <div
                className="col-md-6"
                key={item.title}
                data-reveal
                style={{ "--reveal-delay": `${(index % 2) * 100}ms` }}
              >
                <article className="kili-feature h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="routes" className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal>
            <div className="col-lg-8">
              <p className="eyebrow">Routes</p>
              <h2 className="mb-3">One path, two paces</h2>
              <p className="page-lead mb-0">
                Meru climbs follow the Momella Route. You choose three or four
                days based on fitness, time, and how gently you want to move.
              </p>
            </div>
          </div>

          <div className="kili-routes">
            {routes.map((route, index) => (
              <article
                className={
                  index % 2 === 1
                    ? "kili-route kili-route--flip"
                    : "kili-route"
                }
                key={route.id}
                id={route.id}
                data-reveal={index % 2 === 1 ? "right" : "left"}
              >
                <div className="kili-route__top">
                  <div className="kili-route__media">
                    <img src={route.image} alt={route.imageAlt} />
                  </div>
                  <div className="kili-route__intro">
                    <p className="kili-route__meta mb-2">
                      {route.days}
                      <span aria-hidden="true"> · </span>
                      {route.difficulty}
                    </p>
                    <h3 className="h3 mb-3">{route.name}</h3>
                    <p className="kili-route__summary">{route.summary}</p>
                    <p className="kili-route__body-copy mb-0">{route.body}</p>
                  </div>
                </div>

                <div className="kili-route__panel">
                  <dl className="kili-route__facts">
                    <div>
                      <dt>Best for</dt>
                      <dd>{route.bestFor}</dd>
                    </div>
                    <div>
                      <dt>Start gate</dt>
                      <dd>{route.startGate}</dd>
                    </div>
                    <div>
                      <dt>Lodging</dt>
                      <dd>{route.lodging}</dd>
                    </div>
                    <div>
                      <dt>Duration</dt>
                      <dd>{route.days}</dd>
                    </div>
                  </dl>

                  <div className="row g-4">
                    <div className="col-lg-5">
                      <h4 className="kili-route__subhead">On the trail</h4>
                      <ul className="trek-points mb-0">
                        {route.highlights.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-lg-7">
                      <h4 className="kili-route__subhead">Sample outline</h4>
                      <ol className="kili-itinerary list-unstyled mb-0">
                        {route.itinerary.map((step) => (
                          <li key={step.day}>
                            <strong>{step.day}</strong>
                            <span>{step.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  <p className="kili-route__note mb-3">{route.notes}</p>
                  <Link className="text-link" to="/contact">
                    Ask about {route.name}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5" data-reveal>
            <div className="col-lg-8">
              <p className="eyebrow">Before you climb</p>
              <h2 className="mb-0">Practical notes from the desk</h2>
            </div>
          </div>

          <div className="row g-4">
            {practical.map((item, index) => (
              <div
                className="col-md-4"
                key={item.title}
                data-reveal
                style={{ "--reveal-delay": `${index * 90}ms` }}
              >
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-close" data-reveal="fade">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="eyebrow eyebrow--light">Plan your climb</p>
              <h2 className="mb-3">Tell us if Meru stands alone or leads into Kilimanjaro.</h2>
              <p className="mb-0 home-close__copy">
                We reply with a day by day outline, hut details, and a clear
                price for your group size.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="btn btn-mk-gold" to="/contact">
                Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Meru;
